import { useEffect, useRef } from "react";
import { useDmsPreview } from "./DmsPreviewProvider";

/**
 * DmsBridge — connects the DMS provider's "show" actions to AppShell's
 * right-panel tab state.
 *
 * When form code calls `dms.show()`, this component detects the change
 * (dms.open transitions to true) and calls onShowRequested() so AppShell
 * can set rightPanelTab="dms" to make the DMS tab active.
 *
 * Conversely, when the user closes the right panel (rightPanelTab=null
 * or switches to a different tab), we DON'T clear the dms request — the
 * doc stays loaded in case they reopen the DMS tab. Only an explicit
 * dms.hide() call clears it.
 *
 * Renders nothing.
 *
 * Props:
 *   onShowRequested:  () => void   — called when dms.show() fires
 *
 * Usage in AppShell:
 *   <DmsBridge onShowRequested={() => setRightPanelTab("dms")} />
 */
export default function DmsBridge({ onShowRequested }) {
  const dms = useDmsPreview();
  const wasOpenRef = useRef(false);

  useEffect(() => {
    // Detect transition from closed → open (i.e. someone called dms.show())
    if (dms.open && !wasOpenRef.current) {
      if (onShowRequested) onShowRequested();
    }
    wasOpenRef.current = dms.open;
  }, [dms.open, onShowRequested]);

  return null;
}
